import React, { useCallback, useEffect, useState } from 'react';
import { Calculator as CalcIcon, ArrowRight, ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import CalculatorModal from './CalculatorModal';
import './CalculatorRecentCard.css';

const HISTORY_KEY = 'revstar_calculator_history';
const COLLAPSE_KEY = 'revstar_calculator_card_collapsed';
const SHOW_COUNT = 4;

function readHistory() {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function formatNumber(value) {
  if (value === '' || value === null || value === undefined) return '0';
  const str = String(value);
  const [intPart, decPart] = str.split('.');
  const negative = intPart.startsWith('-');
  const absInt = negative ? intPart.slice(1) : intPart;
  const grouped = absInt.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return (negative ? '-' : '') + grouped + (decPart !== undefined ? '.' + decPart : '');
}

/**
 * Overview-rail card: shows the merchant's recent calculator entries
 * as a stack of mini-cards. Click anywhere on the card opens the
 * calculator modal. If the merchant has no history yet, shows a
 * gentle prompt to try it.
 */
const CalculatorRecentCard = () => {
  const [history, setHistory] = useState(() => readHistory());
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(COLLAPSE_KEY) === '1'; } catch { return false; }
  });

  const toggleCollapsed = () => {
    setCollapsed((c) => {
      const next = !c;
      try { localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0'); } catch {}
      return next;
    });
  };

  const refresh = useCallback(() => {
    setHistory(readHistory());
  }, []);

  useEffect(() => {
    // Cross-tab updates
    const onStorage = (e) => { if (e.key === HISTORY_KEY) refresh(); };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [refresh]);

  // Re-read whenever modal closes (most likely time history changed)
  useEffect(() => { if (!open) refresh(); }, [open, refresh]);

  const recent = history.slice(0, SHOW_COUNT);

  return (
    <>
      <section className={`ov-card rsc-calc-card ${collapsed ? 'is-collapsed' : ''}`} aria-label="Calculator">
        <header className="rsc-calc-card__head">
          <button
            type="button"
            className="rsc-calc-card__toggle"
            onClick={toggleCollapsed}
            aria-expanded={!collapsed}
            aria-label={collapsed ? 'Expand calculator' : 'Collapse calculator'}
          >
            <span className="rsc-calc-card__icon" aria-hidden="true">
              <CalcIcon size={16} strokeWidth={2} />
            </span>
            <span className="rsc-calc-card__heads">
              <span className="rsc-calc-card__title">Calculator</span>
              <span className="rsc-calc-card__sub">
                {recent.length === 0 ? 'Quick arithmetic with history' : `Recent · ${history.length}`}
              </span>
            </span>
            <span className="rsc-calc-card__chevron" aria-hidden="true">
              <ChevronDown size={16} strokeWidth={2} />
            </span>
          </button>
          <button
            type="button"
            className="rsc-calc-card__open"
            onClick={(e) => { e.stopPropagation(); setOpen(true); }}
            aria-label="Open calculator"
          >
            Open
          </button>
        </header>

        <div className="rsc-calc-card__collapse">
          <div className="rsc-calc-card__collapse-inner">
            {recent.length > 0 ? (
              <ul className="rsc-calc-card__list" role="list">
                {recent.map((h) => (
                  <li key={h.id}>
                    <button
                      type="button"
                      className="rsc-calc-card__entry"
                      onClick={() => setOpen(true)}
                    >
                      <span className="rsc-calc-card__expr">{h.expression}</span>
                      <span className="rsc-calc-card__eq" aria-hidden="true">=</span>
                      <span className="rsc-calc-card__result">{formatNumber(h.result)}</span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <button
                type="button"
                className="rsc-calc-card__empty"
                onClick={() => setOpen(true)}
              >
                Try a calculation — it'll show up here.
              </button>
            )}

            {history.length > SHOW_COUNT && (
              <Link to="/utilities/calculator" className="rsc-calc-card__more">
                See all <ArrowRight size={12} aria-hidden="true" />
              </Link>
            )}
          </div>
        </div>
      </section>

      <CalculatorModal open={open} onClose={() => setOpen(false)} />
    </>
  );
};

export default CalculatorRecentCard;
